<template>
  <div>
    <div class="row items-center q-pa-md">
      <h6 class="q-my-none">
          {{ title }}
      </h6>

      <div v-if="sequence">
        <q-btn
          color="danger"
          size="sm"
          class="q-ml-sm"
          :disable="disabled"
          :label="$t('Cancel this wave')"
          no-caps
          @click="handleDeactivate"
        />

        <q-btn
          color="dark"
          size="sm"
          class="q-ml-sm"
          :label="$t('Open Inventory Document')"
          no-caps
          @click="handleRelocate"
        />
      </div>

        <div class="col q-px-xs">
            <slot />
        </div>

        <div v-if="sequence">
          <portal
            v-if="portal"
            :to="portal"
          >
            <q-btn
              color="dark"
              size="sm"
              class="q-mr-sm"
              no-caps
              :label="$t('Choose another wave')"
              @click="handleBackToSequence"
            />
          </portal>

          <q-btn
            v-else
            color="dark"
            size="sm"
            no-caps
            class="q-mr-sm"
            :label="$t('Choose another wave')"
            @click="handleBackToSequence"
          />
        </div>

        <q-btn
          color="dark"
          size="sm"
          class="q-mr-sm"
          no-caps
          :label="$t('Filter')"
          @click="openCloseFilters"
        />

        <q-btn-dropdown
          color="light-blue-9"
          :label="$t('Bulk')"
          no-caps
          size="sm"
        >
          <q-list>
            <q-item
              clickable
              v-close-popup
              :disable="!hasRejectedTasks"
              @click="handleRejectedToNew"
            >
              <q-item-section>
                <q-item-label>
                  {{ $t('Rejected to new') }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
    </div>

    <filter-collapse
      :is-open="isOpen"
      :options="{
        defaultFilter: filter,
        fields: activatedFields,
        values: {
          states: statuses
            },
            style: {
              noGroups: true
            }
        }"
      @submit="handleFiltersSubmit"
      @close="openCloseFilters"
    />

    <q-table
      class="sticky-header-table"
      row-key="id"
      :rows-per-page-label="$t('Rows per page')"
      :rows="tasks"
      :columns="columns"
      v-model:pagination="pagination"
      :loading="isTasksLoading"
      :filter="tableFilter"
      @request="onRequest"
      virtual-scroll
      binary-state-sort
      flat
    >
      <template v-slot:loading>
        <q-inner-loading
          showing
          color="primary"
        />
      </template>

      <template v-slot:body="props">
        <q-tr
          :props="props"
          class="clickable"
        >
          <q-td
            key="id"
            :props="props"
            class="text-subtitle1"
          >
            <strong>{{ props.row.id }}</strong>
          </q-td>

            <q-td
              key="state"
              :props="props"
              class="text-subtitle1"
            >
              <q-badge
                :color="stateColor[props.row.state]"
                :label="$t(props.row.state)"
              />
            </q-td>

            <q-td
              key="executive"
              :props="props"
            >
              <span v-if="props.row._embedded && props.row._embedded.executive">
                {{ props.row._embedded.executive.name }}
              </span>

                <span v-else>--</span>
            </q-td>

            <q-td
              key="updated"
              :props="props"
            >
              {{ $moment(props.row.updated.date).format('HH:mm') }}
            </q-td>

            <q-td
              key="place"
              :props="props"
            >
              <span v-if="props.row._embedded?.place">
                <new-picking-place
                  :data="props.row._embedded.place"
                  :type="props.row._embedded.place.type"
                  badge
                />
              </span>

                <span v-else>--</span>
            </q-td>

            <q-td
              key="sequence"
              :props="props"
            >
                {{ getSequenceID(props.row) || '--' }}
            </q-td>

            <q-td
              key="quantity"
              :props="props"
            >
              {{ `${props.row.scannedCount || 0}/${props.row.count || 0}` }}
            </q-td>

            <q-td
              key="product"
              :props="props"
              style="white-space: pre-wrap;"
            >
              <span v-if="props.row._embedded && props.row._embedded.productOffer">
                {{ `${props.row._embedded.productOffer.name} (${props.row._embedded.productOffer.id})` }}
              </span>

                <span v-else>--</span>
            </q-td>

            <q-td
                    key="collision"
                    :props="props"
            >
                <q-chip v-if="ifCollision(props.row)" clickable square icon="error_outline" color="red" text-color="white" @click="handleCollisionClick(props.row)">{{ $t(`Attention`) }}</q-chip>
                <q-tooltip v-if="ifCollision(props.row)">{{ $t('Click to Fix collusion') }}</q-tooltip>
            </q-td>

            <q-td
                key="barcode"
                :props="props"
                auto-width
            >
               <span v-if="props.row.instructions && props.row.instructions.length > 0">
                  <div
                      v-for="instruction in props.row.instructions"
                      :key="instruction"
                      class="col-4 q-pa-sm"
                  >
                    <div   v-if="instruction && instruction.data && instruction.data.barcode">
                        <div
                            v-for="barcode in instruction.data.barcode"
                            :key="barcode"
                            class="col-4 q-pa-sm"
                        >
                        {{barcode}}
                        <br>
                        </div>
                    </div>

                  </div>
                </span>
                <span v-else-if="props.row.eav && props.row.eav['barcode']">
                    {{props.row.eav['barcode']}}
                </span>

                <span v-else>--</span>
            </q-td>

            <q-td
              key="shop"
              :props="props"
            >
              <span v-if="props.row._embedded && props.row._embedded.shop">
                {{ props.row._embedded.shop.name }}
              </span>

                <span v-else>--</span>
            </q-td>
        </q-tr>
      </template>
    </q-table>

    <tasks-patch-modal ref="tasksModal" />
    <revision-collusion-modal ref="revisionCollusion" @submit="editCount"/>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex'

// Utils
import { buildQuery } from '../../utils/query-utils'

// Components
import FilterCollapse from '../filters/FilterCollapse.vue'
import TasksPatchModal from '../modals/TasksPatchModal.vue'
import NewPickingPlace from '@/apps/app/components/picking/new-picking-place.vue'
import RevisionCollusionModal from "@/apps/app/components/modals/RevisionCollusionModal.vue";

function createDate (date) {
  return `${date.getFullYear()}-${(date.getMonth() + 1)}-${date.getDate()}`.split('-').map(Number)
}

function getDate () {
  let from = createDate(new Date())

  from[2] -= 2

  if (from[2] < 1) {
    from[2] = 1
  }

  return from.map(value => value < 10 ? `0${value}` : value).join('-')
}

export default {
  name: 'TasksTableRevision',
  emits: ['activate', 'deactivate', 'back', 'new-task'],
  components: {
    RevisionCollusionModal,
    NewPickingPlace,
    FilterCollapse,
    TasksPatchModal
  },
  props: {
    portal: {
      type: String,
      default () {
        return null
      }
    },
    service: {
      type: Object,
      required: true,
      default () {
        return {}
      }
    },
    queue: {
      type: [String, Number],
      required: true,
      default () {
        return null
      }
    },
    disabled: {
      type: Boolean,
      default () {
        return false
      }
    },
    sequence: {
      type: Object,
      default () {
        return null
      }
    }
  },
  data () {
    return {
      tableFilter: '',
      pagination: {
        page: 1,
        rowsPerPage: 250,
        rowsNumber: 250
      },
      stateColor: {
        new: 'bg-lime-5 text-white',
        confirmed: 'bg-lime-4 text-white',
        complete: 'bg-lime-3 text-white',
        rejected: 'bg-red-4 text-dark'
      },
      columns: [
        {
          label: this.$t('Id'),
          name: 'id',
          align: 'left'
        },
        {
          label: this.$t('Status'),
          name: 'state',
          align: 'left',
          sortable: true
        },
        {
          label: this.$t('Executive'),
          name: 'executive',
          align: 'left',
          sortable: true
        },
        {
          label: this.$t('Time'),
          name: 'updated',
          align: 'left',
          sortable: true
        },
        {
          label: this.$t('Location'),
          name: 'place',
          align: 'left',
          sortable: true
        },
        {
          label: this.$t('Sequence'),
          name: 'sequence',
          align: 'left'
        },
        {
          label: this.$t('Quantity'),
          name: 'quantity',
          align: 'left'
        },
        {
          label: this.$t('Product'),
          name: 'product',
          align: 'left'
        },
        {
          label: '',
          name: 'collision',
          align: 'left'
        },
        {
          label: this.$t('Barcode'),
          name: 'barcode',
          align: 'left'
        },
        {
          label: this.$t('Shop'),
          name: 'shop',
          align: 'left',
          sortable: true
        }
      ],
      isOpen: false,
      activatedFields: [
        'updated.from',
        'updated.to',
        'shop',
        'executive',
        'reserve',
        'state',
        'place',
        'productOffer',
        'count.from',
        'count.to',
        'scannedCount.from',
        'scannedCount.to'
      ],
      filter: [
        { type: 'gte', field: 'updated', value: getDate() }
      ],
      tasks: [],
      totalPages: 1,
      isTasksLoading: false,
      statuses: [
        { id: 'new', title: this.$t('New') },
        { id: 'confirmed', title: this.$t('Confirmed') },
        { id: 'complete', title: this.$t('Complete') },
        { id: 'rejected', title: this.$t('Rejected') }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'appOptions'
    ]),
    hasRejectedTasks () {
      return this.tasks.find(x => x.state === 'rejected')
    },
    title () {
      if (this.sequence) {
        return `${this.$t('Wave')}: ${this.sequence.id}`
      }

      return this.$t('Tasks')
    }
  },
  mounted () {
    this.onRequest()
  },
  methods: {
    ...mapMutations([
      'setInventoryDocument',
    ]),
    handleRejectedToNew () {
      const queue = this.tasks.filter(x => x.state === 'rejected')
      const data = {
        queue,
        title: this.$t('Move all rejected tasks to new'),
        description: this.$t('Please wait while process finish!'),
        value: { state: 'new' },
        middlewares: {
          onError () {},
          onSave: (task) => {
            this.updateTasks(task)
          }
        }
      }

      this.$refs.tasksModal.open(this.service, data)
    },
    getSequenceID (task) {
      if (task._embedded.sequence) {
        return task._embedded.sequence.id
      }

      return false
    },
    handleActivate () {
      this.$emit('activate', this.sequence.id)
    },
    handleDeactivate () {
      this.$emit('deactivate', this.sequence.id)
    },
    handleBackToSequence () {
      this.$emit('back')
    },
    handleFiltersSubmit (filter) {
      this.filter = filter
      this.isOpen = false
      this.onRequest({ pagination: { page: 1 } })
    },
    openCloseFilters () {
      this.isOpen = !this.isOpen
    },
    onRequest (data = {}) {
      this.pagination = data.pagination || {}
      const query = buildQuery(this.pagination)

      if (!query.page) {
        query.page = 1
      }

      if (!query.per_page) {
        query.per_page = 250
      }

      if (!query.filter) {
        query.filter = []
      }

      query.filter = [
        ...query.filter,
        ...this.filter
      ]

      if (!query['order-by']) {
        query['order-by'] = [
          { type: 'field', field: 'created', direction: 'desc' }
        ]
      }

      if (this.sequence && !query.filter.find(x => x.field === 'sequence')) {
        query.filter.push({ type: 'eq', field: 'sequence', value: this.sequence.id })
      }

      if (!query.filter.find(x => x.field === 'state')) {
        query.filter.push({ type: 'in', field: 'state', values: ['new', 'confirmed', 'complete', 'rejected'] })
      }

      if (!query.filter.find(x => x.field === 'queue')) {
        query.filter.push({ type: 'eq', field: 'queue', value: this.queue })
      }

      this.isTasksLoading = true

      return this.service.getAll(query)
        .then(({ items, totalItems, page, totalPages }) => {
          this.pagination = {
            page,
            ...this.pagination,
            rowsNumber: totalItems
          }

          this.totalPages = totalPages

            this.tasks = items

        })
        .finally(() => {
          this.isTasksLoading = false
        })
    },
    updateTasks (task) {
      if (!task._embedded || !task._embedded.queue || task._embedded.queue.id !== this.queue) {
        return
      }

      if (this.sequence && task._embedded.sequence && task._embedded.sequence.id !== this.sequence.id) {
        return
      }

      let isExist = false

      this.tasks = this.tasks.map(t => {
        if (t.id === task.id) {
          isExist = true
          return task
        }

        return t
      })

      if (!isExist) {
        this.tasks = [task, ...this.tasks]
        this.$emit('new-task', task)
      }
    },
    update (id) {
      if (this.isTasksLoading) {
        return
      }

      const query = {
        per_page: 1,
        page: 1,
        filter: [
          { type: 'eq', field: 'id', value: id }
        ]
      }

      return this.service.getAll(query)
        .then(({ items }) => {
          const task = items[0]

          if (!task) {
            return null
          }

          this.updateTasks(task)
          return task
        })
    },
    handleAssign (id, executive) {
      return this.service.save({ executive: executive.id }, id)
    },
    handleStateChange (id, state) {
      return this.service.save({ state }, id)
        .then(task => {
          this.tasks = this.tasks.filter(t => t.id !== id)
          this.tasks = [task, ...this.tasks]
        })
    },
    ifCollision (item) {
      if(item.eav && item.eav['storage-revision-storage-location-collusion'] && item.eav['storage-revision-storage-location-collusion'] === 'collusion_found') {
        return true
      }
      return false
    },
    handleCollisionClick (item) {
      this.$refs.revisionCollusion.show(item)
    },
    editCount (quantity, item) {
      if(quantity !== item.scannedCount) {
        const result = {
          scannedCount: quantity,
          eav: {['storage-revision-storage-location-collusion'] : "collusion_confirmed"}
        }
        return this.$service.pickingTask.save(result, item.id)
          .then(()=> {
            this.totalExpected = 0
            this.totalFound = 0
            this.totalPositive = 0
            this.totalNegative = 0
            this.onRequest()
          })
      } else {
        const result = {
          eav: {['storage-revision-storage-location-collusion'] : "collusion_confirmed"}
        }
        return this.$service.pickingTask.save(result, item.id)
          .then(()=> {
            this.totalExpected = 0
            this.totalFound = 0
            this.totalPositive = 0
            this.totalNegative = 0
            this.onRequest()
          })
      }
    },
    handleRelocate () {
      const query = buildQuery({})

      query.filter = [
        {
          field: 'sequence',
          type: 'eq',
          value: this.sequence.id
        },
      ]

      return this.$service.storageTasksFinishedLocation.getAll(query)
        .then(({ page, totalItems, items }) => {
          if(items.length === 0) {
            return
          }
          if(items[0] && items[0]._embedded && items[0]._embedded.inventory) {
            const inventory = items[0]._embedded.inventory.id
            this.setInventoryDocument(items[0]._embedded.inventory)
            this.$router.push('/documents/inventory-documents/entity/' + inventory)
          }
        })
    }
  }
}
</script>
